<template>
  <div class="testing-preview">
    <div v-if="!data || data.length === 0" class="no-data">
      <p>No testing data detected in the uploaded file</p>
    </div>
    
    <div v-else class="data-table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Test Cert ID</th>
            <th>Line ID</th>
            <th>Micron (μm)</th>
            <th>Yield (%)</th>
            <th>Lab</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(test, index) in data" :key="index">
            <td>
              <input 
                type="text" 
                v-model="test.TestCertID" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.TestCertID }"
              >
              <div v-if="validationErrors[index]?.TestCertID" class="error-message">
                {{ validationErrors[index].TestCertID }}
              </div>
            </td>
            <td>
              <input 
                type="text" 
                v-model="test.LineID" 
                class="table-input"
                :class="{ 'has-error': validationErrors[index]?.LineID }"
              >
              <div v-if="validationErrors[index]?.LineID" class="error-message">
                {{ validationErrors[index].LineID }}
              </div>
            </td>
            <td>
              <input 
                type="number" 
                step="0.1"
                v-model.number="test.Micron_um" 
                class="table-input"
              >
            </td>
            <td>
              <input 
                type="number" 
                step="0.1"
                v-model.number="test.Yield_pct" 
                class="table-input"
              >
            </td>
            <td>
              <input 
                type="text" 
                v-model="test.Lab" 
                class="table-input"
              >
            </td>
            <td class="actions-cell">
              <button class="btn-icon" @click="expandTest(index)" :title="test.expanded ? 'Collapse' : 'Expand'">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline v-if="!test.expanded" points="6 9 12 15 18 9"></polyline>
                  <polyline v-else points="18 15 12 9 6 15"></polyline>
                </svg>
              </button>
              <button class="btn-icon delete" @click="removeTest(index)" title="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 6h18"></path>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <!-- Expanded Test Details -->
      <div v-for="(test, index) in data" :key="`details-${index}`" class="expanded-details" v-show="test.expanded">
        <div class="details-grid">
          <div class="detail-group">
            <label>Staple Length (mm)</label>
            <input type="number" step="0.1" v-model.number="test.StapleLength_mm" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Staple Strength (Nktex)</label>
            <input type="number" step="0.1" v-model.number="test.StapleStrength_Nktex" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>VM Content (%)</label>
            <input type="number" step="0.1" v-model.number="test.VMContent_pct" class="detail-input">
          </div>
          
          <div class="detail-group">
            <label>Color</label>
            <input type="text" v-model="test.Color" class="detail-input">
          </div>
        </div>
      </div>
    </div>
    
    <div class="add-item-row">
      <button class="btn-add" @click="addNewTest">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Test
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      validationErrors: [],
      testTemplate: {
        TestCertID: '',
        LineID: '',
        Micron_um: null,
        Yield_pct: null,
        StapleLength_mm: null,
        StapleStrength_Nktex: null,
        VMContent_pct: null,
        Color: '',
        Lab: 'AWTA',
        expanded: false
      }
    };
  },
  
  watch: {
    data: {
      handler(newData) {
        // Initialize expanded property if not present
        if (newData) {
          newData.forEach(test => {
            if (test.expanded === undefined) {
              this.$set(test, 'expanded', false);
            }
          });
        }
        
        // Reset validation errors when data changes
        this.validationErrors = Array(newData?.length || 0).fill(null);
        
        // Emit updated data
        this.$emit('update', newData);
      },
      deep: true,
      immediate: true
    }
  },
  
  methods: {
    expandTest(index) {
      if (this.data[index]) {
        this.$set(this.data[index], 'expanded', !this.data[index].expanded);
      }
    },
    
    removeTest(index) {
      if (confirm('Are you sure you want to remove this test?')) {
        const updatedData = [...this.data];
        updatedData.splice(index, 1);
        this.$emit('update', updatedData);
      }
    },
    
    addNewTest() {
      const updatedData = [...this.data];
      updatedData.push({...this.testTemplate});
      this.$emit('update', updatedData);
    },
    
    validateTests() {
      let isValid = true;
      const errors = Array(this.data.length).fill(null).map(() => ({}));
      
      this.data.forEach((test, index) => {
        // Validate TestCertID
        if (!test.TestCertID) {
          errors[index].TestCertID = 'Test Cert ID is required';
          isValid = false;
        }
        
        // Validate LineID
        if (!test.LineID) {
          errors[index].LineID = 'Line ID is required';
          isValid = false;
        }
      });
      
      this.validationErrors = errors;
      return isValid;
    }
  }
};
</script>

<style scoped>
.testing-preview {
  margin-bottom: 24px;
}

.no-data {
  padding: 40px;
  text-align: center;
  background-color: #f9fafb;
  border-radius: 6px;
  color: #6b7280;
}

.data-table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background-color: #f9fafb;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
}

.table-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.table-input:focus {
  border-color: #3A895F;
  outline: none;
  box-shadow: 0  0 0 2px rgba(58, 137, 95, 0.2);
}

.table-input.has-error {
  border-color: #ef4444;
}

.error-message {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.actions-cell {
  white-space: nowrap;
  width: 80px;
}

.btn-icon {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  margin-right: 4px;
}

.btn-icon:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.btn-icon.delete:hover {
  background-color: #fee2e2;
  color: #b91c1c;
}

.expanded-details {
  background-color: #f9fafb;
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e5e7eb;
  border-top: none;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.detail-group {
  display: flex;
  flex-direction: column;
}

.detail-group label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

.detail-input {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.add-item-row {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.btn-add {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: white;
  border: 1px dashed #d1d5db;
  color: #3A895F;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add:hover {
  background-color: #f9fafb;
  border-color: #3A895F;
}
</style>
